/**
 * Brings the first error of a refused full-page form save into view.
 *
 * A refused save re-renders the page with its Form errors in one block near the top and each Field error under its field
 * (docs/adr/0007-validation-re-renders-return-422.md), and the form keeps its scroll position, so a save from the bottom of a
 * long form shows nothing. After a refusal this picks the first error in document order, in the refused form or on the page when
 * the form has none. An error is a non-empty `[role="alert"]` block or an element with `aria-invalid="true"`:
 *
 * - A `role="alert"` block is scrolled just far enough that it sits at the top of the nearest scroller's visible area, or left
 *   alone when it is already in view. Focus stays where it was.
 * - An invalid field takes focus, which scrolls it into view, so no separate scroll runs. A rich-text editor's editable area is
 *   focused once the editor has booted.
 *
 * Hidden elements are skipped. A refusal rendered inside a Turbo Frame is left alone. Focus the page had before the render comes
 * back through `focus-restore-policy.ts`, which runs first.
 */

import { isContentEditableSurface } from "./focus-restore-selection";
import { hasEmptyBody, isFrameSubmission, type SubmitDetail } from "./turbo-submit-event";

/** The refused form, by its `id` (empty when it has none), until the page that shows the refusal renders. */
interface Note {
   readonly formId: string;
}

const ALERT_SELECTOR = '[role="alert"]';
const ERROR_SELECTOR = `${ALERT_SELECTOR}, [aria-invalid="true"]`;

/** The host of a rich-text editor, and the event it dispatches once its editable area is live. */
const RICH_TEXT_HOST_SELECTOR = '[data-controller~="rich-text-editor"]';
const RICH_TEXT_READY_EVENT = "rich-text-editor:ready";

let note: Note | null = null;
let stopWaitingForEditor: (() => void) | null = null;

function clearNote(): void {
   note = null;
   stopWaitingForEditor?.();
}

function onSubmitEnd(event: Event): void {
   const { formSubmission, fetchResponse } = (event as CustomEvent<SubmitDetail>).detail ?? {};
   if (!formSubmission || !fetchResponse || !fetchResponse.isHTML || fetchResponse.succeeded) {
      return;
   }
   if (isFrameSubmission(formSubmission)) {
      return;
   }

   const formElement = formSubmission.formElement;
   const refusal: Note = { formId: formElement instanceof Element ? (formElement.getAttribute("id") ?? "") : "" };
   note = refusal;
   void hasEmptyBody(fetchResponse).then((empty) => {
      if (empty && note === refusal) {
         clearNote();
      }
   });
}

function isRendered(element: Element): boolean {
   for (let node: Element | null = element; node; node = node.parentElement) {
      if (node.hasAttribute("hidden") || getComputedStyle(node).display === "none") {
         return false;
      }
   }
   return true;
}

function isShownError(candidate: HTMLElement): boolean {
   const hasMessage = !candidate.matches(ALERT_SELECTOR) || Boolean(candidate.textContent?.trim());
   return hasMessage && isRendered(candidate);
}

function firstShownError(root: ParentNode): HTMLElement | null {
   for (const candidate of root.querySelectorAll<HTMLElement>(ERROR_SELECTOR)) {
      if (isShownError(candidate)) {
         return candidate;
      }
   }
   return null;
}

/** The first error in the refused form, else the first one anywhere on the page (the `<page-errors />` block). */
function findError(formId: string): HTMLElement | null {
   const form = formId ? document.querySelector(`#${CSS.escape(formId)}`) : null;
   return (form && firstShownError(form)) ?? firstShownError(document);
}

function isVerticalScroller(element: HTMLElement): boolean {
   const overflowY = getComputedStyle(element).overflowY;
   return (overflowY === "auto" || overflowY === "scroll") && element.scrollHeight > element.clientHeight;
}

function nearestScroller(element: HTMLElement): HTMLElement | null {
   for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
      if (isVerticalScroller(ancestor)) {
         return ancestor;
      }
   }
   return document.scrollingElement as HTMLElement | null;
}

/** The scroller's visible area, as the viewport's top edge and its height. */
function visibleBox(scroller: HTMLElement): { top: number; height: number } {
   if (scroller === document.scrollingElement) {
      return { top: 0, height: window.innerHeight };
   }
   return { top: scroller.getBoundingClientRect().top, height: scroller.clientHeight };
}

function reveal(error: HTMLElement): void {
   const scroller = nearestScroller(error);
   if (!scroller) {
      return;
   }

   const box = visibleBox(scroller);
   const offset = error.getBoundingClientRect().top - box.top;
   if (offset >= 0 && offset < box.height) {
      return;
   }
   scroller.scrollTop += offset;
}

/**
 * Focuses an invalid field. A rich-text editor's editable area takes no focus until the editor has booted, which is after this
 * render, so focus waits for the editor's ready event, unless the person has moved focus elsewhere in the meantime.
 */
function focusField(field: HTMLElement): void {
   const host = field.closest(RICH_TEXT_HOST_SELECTOR);
   const candidate: Element = field;
   if (!host || isContentEditableSurface(candidate)) {
      field.focus();
      return;
   }

   const focusAtRender = document.activeElement;
   const stop = (): void => {
      document.removeEventListener(RICH_TEXT_READY_EVENT, onReady);
      stopWaitingForEditor = null;
   };
   const onReady = (event: Event): void => {
      if (event.target !== host) {
         return;
      }
      stop();
      if (document.activeElement === focusAtRender) {
         field.focus();
      }
   };
   document.addEventListener(RICH_TEXT_READY_EVENT, onReady);
   stopWaitingForEditor = stop;
}

function onRender(): void {
   const refusal = note;
   clearNote();
   if (!refusal) {
      return;
   }

   const error = findError(refusal.formId);
   if (!error) {
      return;
   }

   if (error.matches(ALERT_SELECTOR)) {
      reveal(error);
   } else {
      focusField(error);
   }
}

/** Installs the reveal on Turbo's document events. Returns an uninstall function (tests). */
export function installRefusedSubmitReveal(): () => void {
   document.addEventListener("turbo:submit-end", onSubmitEnd);
   document.addEventListener("turbo:render", onRender);
   document.addEventListener("turbo:submit-start", clearNote);
   document.addEventListener("turbo:before-visit", clearNote);
   document.addEventListener("turbo:frame-render", clearNote);

   return () => {
      document.removeEventListener("turbo:submit-end", onSubmitEnd);
      document.removeEventListener("turbo:render", onRender);
      document.removeEventListener("turbo:submit-start", clearNote);
      document.removeEventListener("turbo:before-visit", clearNote);
      document.removeEventListener("turbo:frame-render", clearNote);
      clearNote();
   };
}
